/**
 * AssignOfferModal Component
 *
 * Modal form for assigning offers to users.
 * Fields: Reward dropdown, Priority (number input), Reason (text input)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button } from '@/components/common/Modal';
import { Select, Input, TextArea } from '@/components/common/FormField';
import { offerService } from '@/app/services/offerService';
import { rewardService } from '@/app/services/rewardService';
import { partnerService } from '@/app/services/partnerService';
import type { CreateOfferRequest, ManualContext } from '@/app/types/offers';
import { logger } from '@/app/utils/logger';
import './AssignOfferModal.scss';

interface AssignOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  userId: number;
  partnerProductId?: number | null;
}

export const AssignOfferModal: React.FC<AssignOfferModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
  partnerProductId,
}) => {
  const [formData, setFormData] = useState<{
    rewardId: string;
    priority: string;
    reason: string;
    partnerProductId: string;
  }>({
    rewardId: '',
    priority: '1',
    reason: '',
    partnerProductId: partnerProductId?.toString() || '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rewardOptions, setRewardOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'Select a reward...' },
  ]);
  const [partnerOptions, setPartnerOptions] = useState<{ value: string; label: string }[]>([
    { value: '', label: 'Select a partner...' },
  ]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Update partnerProductId when prop changes
  useEffect(() => {
    if (partnerProductId) {
      setFormData((prev) => ({
        ...prev,
        partnerProductId: partnerProductId.toString(),
      }));
    }
  }, [partnerProductId]);

  // Fetch rewards and partners when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        // Fetch active rewards
        const rewardsResponse = await rewardService.queryRewards({
          status: 'ACTIVE',
          limit: 1000,
          offset: 0,
        });
        const rewards = [
          { value: '', label: 'Select a reward...' },
          ...rewardsResponse.rewards
            .sort((a, b) => a.reward_name.localeCompare(b.reward_name))
            .map((r) => {
              // Build label with distinguishing information
              let label = r.reward_name || r.reward_id;
              const parts: string[] = [];

              // Add provider if available (from redemption_config)
              if (r.redemption_config?.provider) {
                parts.push(`(${r.redemption_config.provider})`);
              }

              // Add variant if it's not the default
              if (r.reward_variant && r.reward_variant !== 'default') {
                parts.push(`[${r.reward_variant}]`);
              }

              // Add reward_key if different from name (helps distinguish duplicates)
              if (
                r.reward_key &&
                r.reward_key !== r.reward_name.toLowerCase().replace(/\s+/g, '-')
              ) {
                parts.push(`- ${r.reward_key}`);
              }

              if (parts.length > 0) {
                label = `${r.reward_name || r.reward_id} ${parts.join(' ')}`;
              }

              return {
                value: r.reward_id,
                label: label,
              };
            }),
        ];
        setRewardOptions(rewards);

        // Fetch active partners
        const partnersResponse = await partnerService.queryPartners({
          is_active: true,
          limit: 1000,
          offset: 0,
        });
        const partners = [
          { value: '', label: 'Select a partner...' },
          ...partnersResponse.partners
            .sort((a, b) =>
              (a.doing_business_as || a.partner_name).localeCompare(
                b.doing_business_as || b.partner_name
              )
            )
            .map((p) => ({
              value: p.partner_product_id.toString(),
              label: `${p.doing_business_as || p.partner_name || 'Partner'} (${p.partner_product_id})`,
            })),
        ];
        setPartnerOptions(partners);
      } catch (err) {
        logger.error('Failed to fetch rewards/partners for offer assignment', { error: err });
        setError('Failed to load rewards and partners. Please try again.');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [isOpen]);

  const handleChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    // Validate form
    if (!formData.rewardId) {
      setError('Please select a reward');
      return;
    }
    if (!formData.partnerProductId) {
      setError('Please select a partner');
      return;
    }
    const priority = parseInt(formData.priority, 10);
    if (isNaN(priority) || priority < 1) {
      setError('Priority must be a positive number');
      return;
    }
    if (!formData.reason.trim()) {
      setError('Reason is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const context: ManualContext = {
        assigned_by: 'admin', // TODO: Get from auth context
        reason: formData.reason.trim(),
      };

      const request: CreateOfferRequest = {
        user_id: userId.toString(),
        reward_id: formData.rewardId,
        partner_product_id: parseInt(formData.partnerProductId, 10),
        assignment_strategy: 'MANUAL',
        context_json: context,
        display_priority: parseInt(formData.priority, 10),
      };

      await offerService.createOffer(request);

      if (onSuccess) {
        onSuccess();
      }
      onClose();

      // Reset form
      setFormData({
        rewardId: '',
        priority: '1',
        reason: '',
        partnerProductId: partnerProductId?.toString() || '',
      });
    } catch (err) {
      setError('Failed to assign offer');
      logger.error('Failed to assign offer', { error: err, userId });
    } finally {
      setSaving(false);
    }
  }, [formData, userId, partnerProductId, onSuccess, onClose]);

  const handleClose = useCallback(() => {
    if (!saving) {
      setFormData({
        rewardId: '',
        priority: '1',
        reason: '',
        partnerProductId: partnerProductId?.toString() || '',
      });
      setError(null);
      onClose();
    }
  }, [saving, partnerProductId, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Assign Offer"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Assigning...' : 'Assign Offer'}
          </Button>
        </>
      }
    >
      <div className="assign-offer-modal">
        {error && <div className="assign-offer-error">{error}</div>}

        {loadingOptions && (
          <div className="assign-offer-loading">Loading rewards and partners...</div>
        )}

        <div className="assign-offer-form">
          <Select
            label="Reward"
            value={formData.rewardId}
            onChange={(value) => handleChange('rewardId', value)}
            options={rewardOptions}
            required
            disabled={loadingOptions}
            error={error && !formData.rewardId ? error : undefined}
          />

          <Select
            label="Partner"
            value={formData.partnerProductId}
            onChange={(value) => handleChange('partnerProductId', value)}
            options={partnerOptions}
            required
            disabled={!!partnerProductId || loadingOptions}
            error={error && !formData.partnerProductId ? error : undefined}
          />

          <Input
            label="Priority"
            type="number"
            value={formData.priority}
            onChange={(value) => handleChange('priority', value)}
            required
            error={
              error && (!formData.priority || parseInt(formData.priority, 10) < 1)
                ? error
                : undefined
            }
          />

          <TextArea
            label="Reason"
            value={formData.reason}
            onChange={(value) => handleChange('reason', value)}
            placeholder="Enter reason for assigning this offer..."
            required
            rows={4}
            minLength={10}
            error={error && !formData.reason.trim() ? error : undefined}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AssignOfferModal;
